import { SyncOutlined } from '@ant-design/icons';
import { Input } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useBrazeCRUDActions } from '@mm/state/actions/BrazeCRUDActions';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { selectBrazeConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Select } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './BrazeIntegrations.module.css';
import { BrazeSyncSetup } from './BrazeSyncSetup';

export const BrazeSettings = () => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector((store: StoreState) => store.braze.accounts);
  const segments = useSelector((store: StoreState) => store.braze.segments);

  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const brazeConnections = useSelector(selectBrazeConnections);
  const brazeConnection = brazeConnections?.[0];

  const accessToken = accounts.find(
    account => account.id === brazeConnection?.accessToken?.id,
  );

  const [apiKey, setApiKey] = useState(accessToken?.accessToken);
  const [error, setError] = useState('');
  const [instanceUrl, setInstanceUrl] = useState(accessToken?.instanceUrl);

  const disabledApiKey = brazeConnection && apiKey === accessToken?.accessToken;

  const { getAccounts, getSegments, createAccessToken, updateAcceessToken } =
    useBrazeCRUDActions();
  const { update, create } = useConnectionCRUDActions();

  const { openNewBillingModal2023 } = useModalActions();
  const notMyToken = accessToken?.readOnly;

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.SENDLANE_UPGRADE,
      onSuccessBilling,
    });
  };

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!apiKey || apiKey.length < 25) {
      setError('Invalid API Key');
      return;
    }
    if (!instanceUrl || !instanceUrl.match(/https?:\/\/.+\.braze\.com$/)) {
      setError(
        'Please provide a valid instance URL (e.g., https://your-instance.braze.com)',
      );
      return;
    }

    const createKey = async () => {
      const { id } = await createAccessToken(
        activeFbPageId,
        apiKey,
        instanceUrl,
      );

      if (!brazeConnections.length) {
        await create(activeFbPageId, {
          name: 'Braze Connection',
          connectionKind: 'braze',
          brazeParams: { accessTokenId: id },
        });
      } else if (
        (brazeConnections || []).find(conn => conn.accessToken?.id !== id)
      ) {
        brazeConnections.forEach(connection => {
          update(activeFbPageId, {
            id: connection.id,
            brazeParams: { accessTokenId: id },
          });
        });
      }
    };

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (accessToken) {
        updateAcceessToken(activeFbPageId, accessToken.id, apiKey, instanceUrl);
      } else {
        createKey();
      }
      setError('');
    }
  };

  useEffect(() => {
    getAccounts(activeFbPageId);
  }, []);

  useEffect(() => {
    setApiKey(accessToken?.accessToken);
    setInstanceUrl(accessToken?.instanceUrl);
    retrieveBrazeSegments();
  }, [accessToken]);

  const retrieveBrazeSegments = async () => {
    if (accessToken?.id && hasNativeIntegrationsFeature) {
      await getSegments(activeFbPageId, accessToken.id);
    }
  };

  useEffect(() => {
    if (
      accessToken &&
      !brazeConnections.find(conn => conn.audienceId === null)
    ) {
      create(activeFbPageId, {
        name: 'Braze Connection',
        connectionKind: 'braze',
        brazeParams: { accessTokenId: accessToken.id },
      });
    }
  }, [accessToken, brazeConnections]);

  return (
    <div className={styles.settingsContent}>
      <div>
        <span className={styles.label}>Credentials</span>

        <div className={classNames(styles.box, styles.boxWithHelp)}>
          <div className={styles.boxContent}>
            <div className={styles.credentialsGrid}>
              <div className={styles.credentialRow}>
                <label className={styles.credentialLabel}>REST endpoint</label>
                <div className={styles.credentialInput}>
                  <Input
                    placeholder="Enter your API REST endpoint"
                    css={{
                      container: styles.inputContainer,
                      input: styles.input,
                    }}
                    value={instanceUrl}
                    onChange={e => setInstanceUrl(e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.credentialRow}>
                <label className={styles.credentialLabel}>API Key</label>
                <div className={styles.credentialInput}>
                  <Input
                    placeholder="Enter your API Key"
                    css={{
                      container: styles.inputContainer,
                      input: styles.input,
                    }}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                  />
                </div>
              </div>

              {brazeConnection &&
                accessToken?.accessToken &&
                accessToken?.instanceUrl && (
                  <div className={styles.credentialRow}>
                    <label className={styles.credentialLabel}>
                      All contacts Segment:
                    </label>
                    <div className={styles.credentialInput}>
                      <Select
                        className={styles.listsSelect}
                        defaultValue={accessToken.segmentId}
                        placeholder="Choose"
                        onChange={segmentId =>
                          updateAcceessToken(
                            activeFbPageId,
                            accessToken.id,
                            undefined,
                            instanceUrl,
                            segmentId,
                          )
                        }
                        options={segments.map(loc => ({
                          value: loc.id,
                          label: loc.name,
                        }))}
                        popupClassName={styles.popup}
                        getPopupContainer={triggerNode =>
                          triggerNode.parentElement
                        }
                      />
                      <PrimaryButton
                        className={classNames(
                          'integrations__header-add-button-secondary',
                          'integrations__header-add-button-action',
                          'integrations__header-button-no-margin',
                        )}
                        onClick={_ =>
                          getSegments(activeFbPageId, accessToken.id, true)
                        }
                        leftIcon={<SyncOutlined />}
                      >
                        Refresh lists
                      </PrimaryButton>
                    </div>
                  </div>
                )}

              <div className={styles.connectButtonRow}>
                <PrimaryButton
                  className={styles.primaryButton}
                  onClick={e => connectApiKey(e)}
                  disabled={disabledApiKey}
                >
                  {brazeConnection && disabledApiKey
                    ? 'Connected'
                    : brazeConnection
                    ? 'Update'
                    : 'Connect'}
                </PrimaryButton>
              </div>
            </div>
            <div className={styles.error}>{error}</div>
          </div>
        </div>
      </div>
      {accessToken && (
        <div>
          <span className={styles.label}>Destinations</span>
          <BrazeSyncSetup
            accessToken={accessToken}
            controlsDisabled={!!notMyToken}
          />
        </div>
      )}
    </div>
  );
};
